import { TextArea, TextAreaProps } from '@shared/components';
import { FieldWrapper } from '@shared/components/react-hook-form/shared';
import { ReactElement } from 'react';
import {
  FieldPath,
  FieldValues,
  useController,
  UseControllerProps,
} from 'react-hook-form';

/**
 * We extend the regular TextArea props with UseControllerProps.
 * To make this truly generic (reusable in any react-hook-form, regarless of
 * the type of its FieldValues), we use generics to infer the type of the
 * FieldValues from the value passed into control prop.
 *
 * @see https://github.com/react-hook-form/react-hook-form/discussions/7851#discussioncomment-2219298
 */
export type ReactHookFormTextAreaProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = TextAreaProps & UseControllerProps<TFieldValues, TName>;

function ReactHookFormTextArea<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(props: ReactHookFormTextAreaProps<TFieldValues, TName>): ReactElement {
  const { control, name, rules, shouldUnregister, defaultValue, ...rest } =
    props;
  const {
    field: { onChange, onBlur, name: fieldName, value, ref },
    fieldState: { error },
  } = useController({
    control,
    name,
    rules,
    shouldUnregister,
    defaultValue,
  });

  return (
    <FieldWrapper error={error}>
      <TextArea
        {...rest}
        aria-invalid={error ? 'true' : 'false'}
        name={fieldName}
        validationState={error ? 'invalid' : undefined}
        value={value}
        onChange={props.onChange ? props.onChange : onChange}
        onBlur={(e) => {
          onBlur();
          props.onBlur?.(e);
        }}
        inputRef={ref}
      />
    </FieldWrapper>
  );
}

export default ReactHookFormTextArea;
